import { Button, Grid, Chip } from "@mui/material";
import { Link } from "@mui/icons-material";
import LoadingSpinner from "components/LoadingSpinner";
import useQuickTextTemplates from "./useQuickTextTemplates";

/**
 * Quick text panel above a report note (bubbles from useQuickTextTemplates). Renders nothing when
 * there is nothing to show.
 *
 * @param {string} account - The report's account name.
 * @param {(text: string) => void} onTemplateClick - Called with the clicked bubble's text.
 * @param {boolean} [ownTemplatesOnReviewOnly] - Own templates for Clinic Review reports only.
 * @param {boolean} [disabled] - Bubbles can't be clicked (while a note or report action runs).
 */
const QuickTextTemplates = ({
  account,
  onTemplateClick,
  ownTemplatesOnReviewOnly = false,
  disabled = false,
}) => {
  const {
    defaultOptions,
    userOptions,
    isLoading,
    canManageTemplates,
    isArchived,
  } = useQuickTextTemplates(account, { ownTemplatesOnReviewOnly });

  if (isArchived || (!canManageTemplates && defaultOptions.length === 0)) {
    return null;
  }

  const options = [...defaultOptions, ...userOptions];

  return (
    <div
      className="p-2"
      style={{
        background: "#fff",
        borderRadius: "4px",
        border: "1px solid #E0E0E0",
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        className="d-flex align-items-center"
      >
        <Grid item>
          <span
            style={{
              fontSize: "14px",
              color: "#757575",
              fontWeight: 500,
            }}
          >
            Quick Text Templates
          </span>
        </Grid>

        {canManageTemplates && (
          <Grid item>
            <Button
              variant="text"
              color="secondary"
              size="small"
              startIcon={<Link />}
              onClick={() =>
                window.open("/settings/quick-text-templates", "_blank")
              }
              style={{ padding: 0, fontSize: "14px" }}
            >
              Manage Notes
            </Button>
          </Grid>
        )}
      </Grid>

      {(options.length > 0 || isLoading) && (
        <div className="d-flex flex-wrap mt-2" style={{ gap: "8px" }}>
          {options.map(({ _id, title, description }) => (
            <Chip
              key={_id ?? title}
              label={title}
              size="small"
              color="info"
              variant="outlined"
              onClick={() => onTemplateClick(description)}
              disabled={disabled}
            />
          ))}
          {isLoading && <LoadingSpinner />}
        </div>
      )}
    </div>
  );
};

export default QuickTextTemplates;
